import type { FC, ReactElement, ReactNode } from 'react';

import { SideNavItem } from './side-nav-item';

interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
  tooltip?: string;
  showTooltip?: boolean;
}

const renderItems = ({
  depth = 0,
  items,
  pathname,
}: {
  depth?: number;
  items: Item[];
  pathname?: string | null;
}): ReactElement[] =>
  items.reduce(
    (acc: ReactElement[], item) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
      }),
    [],
  );

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
}: {
  acc: ReactElement[];
  depth: number;
  item: Item;
  pathname?: string | null;
}): Array<ReactElement> => {
  const checkPath = !!(item.path && pathname);
  const partialMatch = checkPath ? pathname.includes(item.path!) : false;
  const exactMatch = checkPath ? pathname === item.path : false;

  if (item.items) {
    acc.push(
      <SideNavItem
        active={partialMatch}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={item.title}
        label={item.label}
        open={partialMatch}
        title={item.title}
      >
        <ul className="tw:flex tw:flex-col tw:gap-1 tw:list-none tw:m-0 tw:p-0">
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
          })}
        </ul>
      </SideNavItem>,
    );
  } else {
    acc.push(
      <SideNavItem
        active={exactMatch}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        key={item.title}
        label={item.label}
        path={item.path}
        title={item.title}
        tooltip={item.tooltip}
        showTooltip={item.showTooltip}
      />,
    );
  }

  return acc;
};

interface SideNavSectionProps {
  items?: Item[];
  pathname?: string | null;
  subheader?: string;
}

export const SideNavSection: FC<SideNavSectionProps> = (props) => {
  const { items = [], pathname, subheader = '', ...other } = props;

  return (
    <ul className="tw:flex tw:flex-col tw:gap-1 tw:list-none tw:m-0 tw:p-0" {...other}>
      {subheader && (
        <li
          style={{
            color: 'var(--nav-section-title-color)',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.66,
            marginBottom: 4,
            marginLeft: 4,
            textTransform: 'uppercase',
          }}
        >
          {subheader}
        </li>
      )}
      {renderItems({ items, pathname })}
    </ul>
  );
};
